import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
}

const questions: QuizQuestion[] = [
  {
    question: "Which Chinese dynasty invented the wok?",
    options: ["Ming Dynasty", "Han Dynasty", "Tang Dynasty", "Song Dynasty"],
    correctAnswer: 1
  },
  {
    question: "What is the main ingredient in Mapo Tofu?",
    options: ["Chicken", "Beef", "Tofu", "Fish"],
    correctAnswer: 2
  },
  {
    question: "How many major regional cuisines are there in China?",
    options: ["Four", "Six", "Eight", "Ten"],
    correctAnswer: 2
  },
  {
    question: "Which ingredient gives Sichuan cuisine its numbing sensation?",
    options: ["Black Pepper", "Chili Oil", "Star Anise", "Sichuan Peppercorns"],
    correctAnswer: 3
  },
  {
    question: "When were chopsticks first used in China?",
    options: ["1000 BCE", "2000 BCE", "3000 BCE", "4000 BCE"],
    correctAnswer: 1
  }
];

export const ChineseFoodQuiz: React.FC = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [showScore, setShowScore] = useState(false);
  const [isAnswered, setIsAnswered] = useState(false);

  const handleAnswerClick = (answerIndex: number) => {
    if (isAnswered) return;
    
    setIsAnswered(true);
    
    if (answerIndex === questions[currentQuestion].correctAnswer) {
      setScore(score + 1);
    }

    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        setIsAnswered(false);
      } else {
        setShowScore(true);
      }
    }, 500);
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setScore(0);
    setShowScore(false);
    setIsAnswered(false);
  };

  return (
    <>
      <Helmet>
        <title>Chinese Food Quiz - Test Your Knowledge | Chinese Food Maps</title>
        <meta name="description" content="Think you know everything about Chinese cuisine? Take our fun quiz and test your knowledge about Chinese food history, facts, and traditions. Challenge yourself and learn something new!" />
        <meta property="og:title" content="Chinese Food Quiz - Test Your Knowledge | Chinese Food Maps" />
        <meta property="og:description" content="Think you know everything about Chinese cuisine? Take our fun quiz and test your knowledge about Chinese food history, facts, and traditions." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://chinesefoodmaps.com/chinese-food-quiz" />
        <meta property="og:image" content="https://cdn-icons-png.flaticon.com/512/2548/2548242.png" />
        <link rel="canonical" href="https://chinesefoodmaps.com/chinese-food-quiz" />
      </Helmet>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 text-red-900">🥢 Chinese Food Quiz</h1>
        
        <div className="bg-white rounded-lg shadow-md p-6 max-w-2xl mx-auto">
          {showScore ? (
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-4">Quiz Complete! 🎉</h2>
              <p className="text-xl mb-6">
                You scored {score} out of {questions.length}!
              </p>
              <button
                onClick={resetQuiz}
                className="px-6 py-3 bg-red-700 text-white rounded-lg hover:bg-red-800 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-sm text-gray-500">
                    Question {currentQuestion + 1} of {questions.length}
                  </span>
                </div>
                <h2 className="text-xl font-semibold mb-6">
                  {questions[currentQuestion].question}
                </h2>
                <div className="space-y-3">
                  {questions[currentQuestion].options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleAnswerClick(index)}
                      disabled={isAnswered}
                      className={`w-full p-4 text-left rounded-lg border-2 transition-all duration-300 ${
                        isAnswered ? 'opacity-50' : 'bg-white hover:bg-red-50'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};